import React from 'react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-tal-blue-600 to-tal-brown-600 px-8 py-12 text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-lg opacity-90">Your Privacy Matters to Us</p>
            <p className="text-sm mt-4 opacity-75">Last Updated: January 1, 2025</p>
          </div>

          <div className="px-8 py-10 space-y-8">
            {/* Introduction */}
            <div className="bg-tal-blue-50 border-l-4 border-tal-blue-500 p-6 rounded-r">
              <p className="text-gray-800 leading-relaxed">
                Tales and Legends (<strong>"we"</strong>, <strong>"our"</strong>, or <strong>"us"</strong>) is committed
                to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your
                information when you use our websites, games, and services (collectively, the <strong>"Services"</strong>).
                Please read this policy carefully. If you do not agree with the terms of this Privacy Policy, please do not
                access the Services.
              </p>
            </div>

            {/* Section 1: Data Controller */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                1. Data Controller
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4 mb-4">
                Tales and Legends is the data controller responsible for your personal information. For privacy-related
                inquiries, you may contact us at:
              </p>
              <div className="ml-8 bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700"><strong>Email:</strong> privacy@tales-and-legends.com</p>
                <p className="text-gray-700"><strong>Website:</strong> www.tales-and-legends.com</p>
              </div>
            </section>

            {/* Section 2: Children's Privacy */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                2. Children's Privacy
              </h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r ml-4">
                <p className="text-gray-800 leading-relaxed font-semibold">
                  Tales and Legends does not knowingly collect Personal Data from children under the age of 13 without
                  verifiable parental consent. If you are a parent or guardian and believe your child has provided us with
                  Personal Data without your consent, please contact us immediately, and we will take steps to delete such
                  information from our systems.
                </p>
              </div>
            </section>

            {/* Section 3: Information We Collect */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                3. Information We Collect
              </h2>

              <div className="space-y-6 ml-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">A. Information You Provide to Us</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    We collect information that you voluntarily provide when using our Services:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>Account Information:</strong> Username, email address, password, and profile information</li>
                    <li><strong>Support Communications:</strong> Name, email, device information, and the content of your support requests</li>
                    <li><strong>Newsletter Subscriptions:</strong> Email address and communication preferences</li>
                    <li><strong>Job Applications:</strong> Resume, work history, contact information, and eligibility to work</li>
                    <li><strong>User Content:</strong> Any content you submit, post, or share through the Services</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">B. Information Collected Automatically</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    When you access our Services, we automatically collect certain information:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>Device Information:</strong> Device type, unique device identifiers, MAC address, mobile network information</li>
                    <li><strong>Device Settings:</strong> Language preference, country code, time zone, carrier information</li>
                    <li><strong>Usage Data:</strong> Player ID, username, gameplay duration, progress, achievements, in-game purchases</li>
                    <li><strong>Advertising IDs:</strong> Apple IDFA, Google Advertising ID for personalized advertising</li>
                    <li><strong>Log Data:</strong> IP address, browser type, pages visited, access times, referring URLs</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">C. Cookies and Tracking Technologies</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    We use cookies and similar tracking technologies to track activity on our Services and store certain information:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>Session Cookies:</strong> Temporary cookies that expire when you close your browser</li>
                    <li><strong>Persistent Cookies:</strong> Remain on your device until deleted or expired</li>
                    <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our Services</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    You can instruct your browser to refuse all cookies or indicate when a cookie is being sent. However,
                    if you do not accept cookies, you may not be able to use some portions of our Services.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 4: Third-Party Services */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                4. Third-Party Services and Integrations
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4 mb-4">
                We may use third-party service providers to help us operate our Services and analyze usage. These third
                parties may collect information as detailed in their respective privacy policies:
              </p>
              <div className="ml-4 space-y-3">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Analytics Services:</h4>
                  <p className="text-gray-700">Google Analytics, Amplitude, and other analytics platforms to understand user behavior</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Advertising Partners:</h4>
                  <p className="text-gray-700">Ad networks for personalized advertising and campaign measurement</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Cloud Infrastructure:</h4>
                  <p className="text-gray-700">Amazon Web Services (AWS) for data storage and processing</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Payment Processors:</h4>
                  <p className="text-gray-700">Apple App Store, Google Play Store, and other payment platforms</p>
                </div>
              </div>
            </section>

            {/* Section 5: Legal Basis for Processing */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                5. Legal Basis for Processing (EU/UK Users)
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4 mb-3">
                If you are located in the European Economic Area or United Kingdom, we process your personal data based on:
              </p>
              <ul className="list-disc pl-10 space-y-2 text-gray-700">
                <li><strong>Consent:</strong> You have given clear consent for us to process your data for specific purposes</li>
                <li><strong>Contract Performance:</strong> Processing is necessary to fulfill our contract with you</li>
                <li><strong>Legal Obligations:</strong> We must process your data to comply with the law</li>
                <li><strong>Legitimate Interests:</strong> Processing is necessary for our legitimate business interests</li>
                <li><strong>Vital Interests:</strong> Processing is necessary to protect someone's life</li>
              </ul>
            </section>

            {/* Section 6: How We Use Your Information */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                6. How We Use Your Information
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4 mb-3">
                We use the information we collect for the following purposes:
              </p>
              <div className="ml-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-tal-blue-50 p-4 rounded-lg border-l-4 border-tal-blue-400">
                  <h4 className="font-semibold text-gray-800 mb-2">Service Provision</h4>
                  <p className="text-gray-700 text-sm">Provide, maintain, and improve our Services</p>
                </div>
                <div className="bg-tal-blue-50 p-4 rounded-lg border-l-4 border-tal-blue-400">
                  <h4 className="font-semibold text-gray-800 mb-2">Customer Support</h4>
                  <p className="text-gray-700 text-sm">Respond to your inquiries and provide assistance</p>
                </div>
                <div className="bg-tal-blue-50 p-4 rounded-lg border-l-4 border-tal-blue-400">
                  <h4 className="font-semibold text-gray-800 mb-2">Personalization</h4>
                  <p className="text-gray-700 text-sm">Customize your experience and provide relevant content</p>
                </div>
                <div className="bg-tal-blue-50 p-4 rounded-lg border-l-4 border-tal-blue-400">
                  <h4 className="font-semibold text-gray-800 mb-2">Communications</h4>
                  <p className="text-gray-700 text-sm">Send updates, newsletters, and promotional materials</p>
                </div>
                <div className="bg-tal-blue-50 p-4 rounded-lg border-l-4 border-tal-blue-400">
                  <h4 className="font-semibold text-gray-800 mb-2">Analytics</h4>
                  <p className="text-gray-700 text-sm">Analyze usage patterns and improve our Services</p>
                </div>
                <div className="bg-tal-blue-50 p-4 rounded-lg border-l-4 border-tal-blue-400">
                  <h4 className="font-semibold text-gray-800 mb-2">Security</h4>
                  <p className="text-gray-700 text-sm">Prevent fraud, enforce our terms, and protect our rights</p>
                </div>
                <div className="bg-tal-blue-50 p-4 rounded-lg border-l-4 border-tal-blue-400">
                  <h4 className="font-semibold text-gray-800 mb-2">Legal Compliance</h4>
                  <p className="text-gray-700 text-sm">Comply with legal obligations and regulations</p>
                </div>
                <div className="bg-tal-blue-50 p-4 rounded-lg border-l-4 border-tal-blue-400">
                  <h4 className="font-semibold text-gray-800 mb-2">Employment</h4>
                  <p className="text-gray-700 text-sm">Evaluate job applications and manage recruitment</p>
                </div>
              </div>
            </section>

            {/* Section 7: Information Sharing */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                7. How We Share Your Information
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4 mb-3">
                We may share your information in the following circumstances:
              </p>
              <div className="ml-4 space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Service Providers</h4>
                  <p className="text-gray-700 leading-relaxed">
                    We share information with third-party vendors who perform services on our behalf, such as hosting,
                    analytics, payment processing, and customer support.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Business Transfers</h4>
                  <p className="text-gray-700 leading-relaxed">
                    In connection with a merger, acquisition, bankruptcy, or sale of assets, your information may be
                    transferred to the acquiring entity.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Legal Requirements</h4>
                  <p className="text-gray-700 leading-relaxed">
                    We may disclose your information if required by law, court order, or government regulation, or if
                    we believe disclosure is necessary to protect our rights or the safety of others.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">With Your Consent</h4>
                  <p className="text-gray-700 leading-relaxed">
                    We may share your information with third parties when you give us permission to do so.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 8: Data Security and Retention */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                8. Data Security and Retention
              </h2>
              <div className="ml-4 space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Security Measures</h4>
                  <p className="text-gray-700 leading-relaxed mb-2">
                    We implement industry-standard security measures to protect your information:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>All data is encrypted in transit using Transport Layer Security (TLS)</li>
                    <li>Data at rest is encrypted using industry-standard encryption protocols</li>
                    <li>Access to personal data is restricted to authorized personnel only</li>
                    <li>Regular security audits and vulnerability assessments</li>
                  </ul>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r mt-4">
                    <p className="text-gray-800 text-sm">
                      <strong>Note:</strong> While we strive to protect your information, no method of transmission over
                      the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Data Retention</h4>
                  <p className="text-gray-700 leading-relaxed">
                    We retain your personal information only for as long as necessary to fulfill the purposes outlined
                    in this Privacy Policy, unless a longer retention period is required by law. When data is no longer
                    needed, we securely delete or anonymize it.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Data Storage Location</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Your information is stored on servers in the United States and may be transferred to and processed
                    in countries other than your own. By using our Services, you consent to the transfer of your
                    information to the United States and other countries.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 9: Your Rights and Choices */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                9. Your Rights and Choices
              </h2>

              <div className="ml-4 space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">A. General User Rights</h3>
                  <div className="space-y-3">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-800 mb-2">Access and Correction</h4>
                      <p className="text-gray-700 text-sm">
                        You may request access to your personal information and request corrections or updates.
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-800 mb-2">Right to be Forgotten</h4>
                      <p className="text-gray-700 text-sm">
                        You may request deletion of your personal data, subject to certain exceptions.
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-800 mb-2">Email Preferences</h4>
                      <p className="text-gray-700 text-sm">
                        You can opt out of promotional emails by clicking the unsubscribe link in any email.
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-800 mb-2">Cookie Controls</h4>
                      <p className="text-gray-700 text-sm">
                        You can manage cookie preferences through your browser settings.
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-800 mb-2">Targeted Advertising Opt-Out</h4>
                      <p className="text-gray-700 text-sm">
                        You can opt out of targeted advertising through your device settings (iOS: Limit Ad Tracking,
                        Android: Opt out of Ads Personalization).
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">B. California Residents' Rights</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    If you are a California resident, you have additional rights under the California Consumer Privacy
                    Act (CCPA):
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Right to know what personal information we collect, use, and disclose</li>
                    <li>Right to request deletion of your personal information</li>
                    <li>Right to opt out of the sale of personal information (we do not sell personal information)</li>
                    <li>Right to non-discrimination for exercising your privacy rights</li>
                    <li>Right to request disclosure of information shared with third parties for marketing purposes ("Shine the Light")</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    California residents under 18 years of age may request removal of content they posted on our Services.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">C. EU/UK Data Subject Rights</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    If you are located in the European Economic Area or United Kingdom, you have the following rights
                    under GDPR:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>Right of Access:</strong> Obtain confirmation that we process your data and receive a copy</li>
                    <li><strong>Right to Rectification:</strong> Request correction of inaccurate personal data</li>
                    <li><strong>Right to Erasure:</strong> Request deletion of your personal data under certain conditions</li>
                    <li><strong>Right to Restriction:</strong> Request restriction of processing under certain circumstances</li>
                    <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                    <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                    <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
                    <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection authority</li>
                  </ul>
                </div>

                <div className="bg-tal-blue-50 border-l-4 border-tal-blue-500 p-6 rounded-r">
                  <h4 className="font-semibold text-gray-800 mb-2">Exercising Your Rights</h4>
                  <p className="text-gray-700 leading-relaxed">
                    To exercise any of these rights, please contact us at privacy@tales-and-legends.com. We will respond
                    to your request within one month (30 days) of receipt. We may ask you to verify your identity before
                    processing your request.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 10: Do Not Track */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                10. Do Not Track Signals
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4">
                Some browsers incorporate a "Do Not Track" (DNT) feature that signals websites you visit that you do not
                want to have your online activity tracked. As there is not yet a common understanding of how to interpret
                DNT signals, our Services do not currently respond to browser DNT signals.
              </p>
            </section>

            {/* Section 11: International Transfers */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                11. International Data Transfers
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4 mb-3">
                Your information may be transferred to and maintained on servers located outside of your state, province,
                country, or other governmental jurisdiction where data protection laws may differ. If you are located
                outside the United States and choose to provide information to us, please note that we transfer the data
                to the United States and process it there.
              </p>
              <p className="text-gray-700 leading-relaxed ml-4">
                We will take all steps reasonably necessary to ensure that your data is treated securely and in accordance
                with this Privacy Policy.
              </p>
            </section>

            {/* Section 12: Changes to Privacy Policy */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                12. Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4">
                We may update this Privacy Policy from time to time. We will notify you of material changes by posting
                the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this
                Privacy Policy periodically. Your continued use of the Services after any changes constitutes your
                acceptance of the new Privacy Policy.
              </p>
            </section>

            {/* Contact Information */}
            <section className="bg-gray-50 rounded-lg p-6 border-l-4 border-tal-brown-500">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices,
                please contact us at:
              </p>
              <div className="text-gray-700 space-y-1 bg-white p-4 rounded">
                <p className="font-semibold text-lg">Tales and Legends</p>
                <p><strong>Email:</strong> privacy@tales-and-legends.com</p>
                <p><strong>Website:</strong> www.tales-and-legends.com</p>
              </div>
              <p className="text-gray-600 text-sm mt-4">
                We will respond to all requests, inquiries, or concerns within thirty (30) days.
              </p>
            </section>
          </div>

          {/* Footer */}
          <div className="bg-gray-100 px-8 py-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-600">
              <p>Last Updated: January 1, 2025</p>
              <p className="mt-2 sm:mt-0">© {new Date().getFullYear()} Tales and Legends. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
